import { Autocomplete, Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import useSearchLocations from "hooks/api/useSearchLocations";
import EditIcon from "@mui/icons-material/Edit";

const initialLocation = {
    coordinates: {},
    locationInfo: {},
};

export const LocationAutoComplete = ({
    value,
    fieldName,
    disabled = false,
    formControls,
    autoCompleteProps,
    textFieldVariant = "standard",
    textFieldLabel = "Location",
    renderEditControl,
    open,
    emitFullValue = false,
}) => {
    const { control, setValue } = formControls;
    const isOpenControlled = open !== undefined;
    const [openAutocompleteState, setOpenAutocompleteState] = useState(
        value ? false : true,
    );
    const openAutocomplete = isOpenControlled ? open : openAutocompleteState;

    const {
        isSearchLocationsLoading,
        locations,
        fetchLocations,
        setLocations,
    } = useSearchLocations();

    useEffect(() => {
        const locationInfo = {
            coordinates: { ...value?.coordinates },
            locationInfo: { ...value?.locationInfo },
        };
        if (value) setValue(fieldName, emitFullValue ? value : locationInfo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditLocation = () => {
        setValue(fieldName, { ...initialLocation });
        if (!isOpenControlled) setOpenAutocompleteState(true);
    };

    const handleLocationInputChange = (event, value, reason) => {
        if (reason === "clear") {
            setValue(fieldName, { ...initialLocation });
            setLocations([]);
        }
        if (reason === "input") {
            fetchLocations(value);
        }
    };

    return (
        <>
            {openAutocomplete ? (
                <Controller
                    name={"location"}
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                        <Autocomplete
                            filterOptions={(x) => x}
                            fullWidth
                            id={fieldName}
                            loading={isSearchLocationsLoading}
                            loadingText={"Loading locations"}
                            options={locations}
                            disabled={disabled}
                            autoComplete
                            getOptionLabel={(option) =>
                                typeof option === "string"
                                    ? option
                                    : (option?.title ?? "")
                            }
                            isOptionEqualToValue={(option, value) => {
                                if (typeof option === "string") {
                                    return option === value;
                                }
                                return option?.id === value?.id;
                            }}
                            onChange={(_, newValue) => {
                                const locationInfo = {
                                    coordinates: newValue?.coordinates || {},
                                    locationInfo: newValue?.locationInfo || {},
                                };
                                const emitted = emitFullValue
                                    ? newValue || locationInfo
                                    : locationInfo;

                                field.onChange(emitted);
                                setValue(fieldName, emitted);
                            }}
                            onInputChange={handleLocationInputChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant={textFieldVariant}
                                    label={textFieldLabel}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            )}
                            {...autoCompleteProps}
                        />
                    )}
                />
            ) : (
                <TextField
                    InputProps={{
                        endAdornment: (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {renderEditControl ? (
                                    renderEditControl(handleEditLocation)
                                ) : (
                                    <EditIcon
                                        onClick={() => handleEditLocation()}
                                        sx={{ cursor: "pointer" }}
                                    />
                                )}
                            </Box>
                        ),
                    }}
                    sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000",
                            opacity: 1,
                        },
                    }}
                    disabled
                    fullWidth
                    variant={textFieldVariant}
                    name="location"
                    defaultValue={value?.title || undefined}
                    label={textFieldLabel}
                />
            )}
        </>
    );
};

export default LocationAutoComplete;
